import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash2, ChefHat, Flame } from "lucide-react";
import { listRecipes, deleteRecipe } from "../lib/api";
import { computeRecipeTotals } from "../lib/nutrition";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return ""; }
};

export const RecipesList = () => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const data = await listRecipes();
      setRecipes(data);
    } catch (e) {
      toast.error("Impossible de charger les recettes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    try {
      await deleteRecipe(id);
      toast.success("Recette supprimée.");
      setRecipes((prev) => prev.filter((r) => r.id !== id));
    } catch {
      toast.error("Erreur lors de la suppression.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-8 md:p-12" data-testid="recipes-list-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-emerald-700 mb-2">
            Tableau de bord
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-stone-900">
            Mes recettes
          </h1>
          <p className="mt-2 text-stone-500 max-w-xl">
            Calculez les valeurs nutritionnelles de vos préparations à partir des données officielles{" "}
            <span className="text-emerald-700 font-semibold">USDA FoodData Central</span>.
          </p>
        </div>
        <Link to="/recettes/nouvelle">
          <Button
            className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold tracking-wide rounded-lg h-11 px-5"
            data-testid="new-recipe-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle recette
          </Button>
        </Link>
      </div>

      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-44 w-full rounded-2xl bg-stone-100" />
          ))}
        </div>
      )}

      {!loading && recipes.length === 0 && (
        <div
          className="rounded-2xl border border-stone-200 bg-white p-10 sm:p-14 text-center shadow-sm overflow-hidden relative"
          data-testid="empty-recipes"
        >
          <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-50 grid place-items-center text-emerald-700 mb-5">
            <ChefHat className="h-8 w-8" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-stone-900 mb-2">
            Aucune recette pour le moment
          </h2>
          <p className="text-stone-500 max-w-md mx-auto mb-6">
            Créez votre première recette pour calculer automatiquement les calories,
            protéines, glucides, lipides et fibres.
          </p>
          <Button
            onClick={() => navigate("/recettes/nouvelle")}
            className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold tracking-wide rounded-lg"
            data-testid="empty-create-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Créer ma première recette
          </Button>
        </div>
      )}

      {!loading && recipes.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="recipes-grid">
          {recipes.map((r) => {
            const t = computeRecipeTotals(r.ingredients);
            return (
              <div
                key={r.id}
                className="group bg-white border border-stone-200 rounded-2xl p-6 shadow-sm hover:shadow-lg hover:border-emerald-200 transition-all cursor-pointer relative"
                onClick={() => navigate(`/recettes/${r.id}`)}
                data-testid={`recipe-card-${r.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="min-w-0">
                    <h3 className="font-heading text-xl font-bold text-stone-900 truncate">
                      {r.name}
                    </h3>
                    <p className="text-xs text-stone-500 mt-0.5">
                      {r.ingredients.length} ingrédient{r.ingredients.length > 1 ? "s" : ""} · {formatDate(r.created_at)}
                    </p>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => e.stopPropagation()}
                        className="h-8 w-8 text-stone-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`delete-recipe-${r.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer cette recette ?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Cette action est définitive. La recette « {r.name} » sera supprimée.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="cancel-delete">Annuler</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(r.id)}
                          className="bg-red-600 hover:bg-red-700 text-white"
                          data-testid={`confirm-delete-${r.id}`}
                        >
                          Supprimer
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>

                <div className="flex items-baseline gap-2 mb-4">
                  <Flame className="h-5 w-5 text-amber-500" />
                  <div className="text-3xl font-light text-stone-900 tabular-nums">{t.calories}</div>
                  <div className="text-xs uppercase tracking-widest text-stone-400">kcal · {t.weight_g} g</div>
                </div>

                <div className="grid grid-cols-4 gap-2 text-center">
                  {[
                    { label: "Prot.", value: t.protein, color: "text-emerald-700" },
                    { label: "Gluc.", value: t.carbs, color: "text-amber-700" },
                    { label: "Lip.", value: t.fat, color: "text-red-600" },
                    { label: "Fibres", value: t.fiber, color: "text-lime-700" },
                  ].map((m) => (
                    <div key={m.label} className="rounded-lg bg-stone-50 py-2">
                      <div className={`text-sm font-bold ${m.color} tabular-nums`}>{m.value}g</div>
                      <div className="text-[10px] uppercase tracking-wider text-stone-500 mt-0.5">{m.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RecipesList;
